import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, FolderKanban, MoreVertical, Pencil, Trash2, Search, Eye } from "lucide-react";
import ClientProjectDetailDialog from "@/components/ClientProjectDetailDialog";
import { toast } from "sonner";

const STATUSES = ["À démarrer", "En cours", "En pause", "Terminé", "Annulé"];

export default function ClientProjects() {
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();
  const [detailId, setDetailId] = useState(params.get("open"));
  const [detailTab, setDetailTab] = useState(params.get("tab") || "devis");
  const [q, setQ] = useState("");

  const load = () => api.get("/client-projects").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); }, []);

  const schema = [
    { name: "nom", label: "Nom du projet", required: true, col: 2 },
    { name: "client_id", label: "Client", type: "select", options: clients.map((c) => ({ value: c.id, label: c.nom })) },
    { name: "statut", label: "Statut", type: "select", options: STATUSES },
    { name: "date_debut", label: "Date de début", type: "date" },
    { name: "date_fin", label: "Date de fin", type: "date" },
    { name: "budget", label: "Budget (FCFA)", type: "number" },
    { name: "type_construction", label: "Type de construction", type: "select", options: ["Villa", "Immeuble R+1", "Immeuble R+2 et plus", "Duplex", "Extension", "Rénovation", "Bâtiment commercial", "Bâtiment industriel", "Autre"] },
    { name: "surface", label: "Surface (ex. 250 m²)" },
    { name: "localisation", label: "Localisation (ville, quartier)" },
    { name: "responsable", label: "Responsable" },
    { name: "description", label: "Description", type: "textarea" },
    { name: "notes", label: "Notes", type: "textarea" },
  ];

  const save = async (form) => {
    try {
      if (editing) { await api.put(`/client-projects/${editing.id}`, form); toast.success("Projet mis à jour"); }
      else { await api.post("/client-projects", form); toast.success("Projet créé"); }
      load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce projet client ?")) return; await api.delete(`/client-projects/${id}`); toast.success("Supprimé"); load(); };

  const filtered = (items || []).filter((p) => !q || `${p.nom} ${p.client_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Projets clients" subtitle="Projets rattachés aux clients — réutilisables dans opportunités, devis, contrats, factures et livraisons">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-client-project" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau projet</Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="pl-9" data-testid="client-project-filter" />
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={FolderKanban} title="Aucun projet client" subtitle="Créez un projet et associez-le à un client." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead><TableHead>Projet</TableHead><TableHead>Client</TableHead>
              <TableHead>Statut</TableHead><TableHead>Budget</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`client-project-row-${p.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(p.id)}>
                  <TableCell className="font-mono text-xs text-slate-500">{p.numero}</TableCell>
                  <TableCell className="font-semibold">{p.nom}</TableCell>
                  <TableCell className="text-slate-600">{p.client_name || "—"}</TableCell>
                  <TableCell><StatusBadge value={p.statut} /></TableCell>
                  <TableCell className="font-mono text-sm">{(p.budget || 0).toLocaleString()} F</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`client-project-menu-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailId(p.id)} data-testid={`client-project-view-${p.id}`}><Eye className="h-4 w-4 mr-2" />Voir la fiche</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(p.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le projet client" : "Nouveau projet client"}
        schema={schema} initial={editing || { statut: "En cours" }} onSubmit={save} />
      <ClientProjectDetailDialog key={`${detailId}-${detailTab}`} projectId={detailId} initialTab={detailTab}
        onOpenChange={(o) => { if (!o) { setDetailId(null); setDetailTab("devis"); if (params.get("open")) setParams({}); } }} />
    </div>
  );
}
